import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAudioPlayer } from '@/context/AudioContext';
import { useCabinaOpen } from '@/context/CabinaContext';
import MobileFullPlayer from '@/pages/app/components/MobileFullPlayer';

export default function GlobalPlayer() {
  const { currentStation, isPlaying, volume, toggle, setVolume, changeStation, stations, stop, fallbackInfo, dismissFallback, streamError, dismissStreamError } =
    useAudioPlayer();

  const { isCabinaOpen } = useCabinaOpen();
  const [expanded, setExpanded] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [stationSearch, setStationSearch] = useState('');
  const [fallbackVisible, setFallbackVisible] = useState(true);
  const volumeRef = useRef<HTMLDivElement>(null);
  const switcherRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // ── Auto-dismiss del banner de fallback después de 12 segundos ──
  useEffect(() => {
    if (!fallbackInfo) {
      setFallbackVisible(false);
      return;
    }
    setFallbackVisible(true);
    const timer = setTimeout(() => {
      setFallbackVisible(false);
    }, 12000);
    return () => clearTimeout(timer);
  }, [fallbackInfo]);

  // ── Cerrar popups al clickear fuera ──
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (volumeRef.current && !volumeRef.current.contains(e.target as Node)) {
        setShowVolume(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Escape cierra el modal ──
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && expanded) {
        setExpanded(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expanded]);

  if (!currentStation) return null;

  const filteredStations = stations.filter(
    (s) =>
      s.name.toLowerCase().includes(stationSearch.toLowerCase()) ||
      s.frequency.includes(stationSearch) ||
      s.city.toLowerCase().includes(stationSearch.toLowerCase()) ||
      s.category.toLowerCase().includes(stationSearch.toLowerCase()),
  );

  const volumeIcon =
    volume === 0
      ? 'ri-volume-mute-line'
      : volume < 33
        ? 'ri-volume-down-line'
        : volume < 66
          ? 'ri-volume-up-line'
          : 'ri-volume-up-fill';

  const showFallbackBanner = fallbackInfo && fallbackVisible;
  const showStreamErrorBanner = streamError && !currentStation;

  return (
    <>
      {/* Stream Error Banner — Desktop */}
      {showStreamErrorBanner && (
        <div className="hidden md:block fixed z-[60] bottom-6 right-6">
          <div className="bg-red-50 border border-red-200/70 rounded-xl px-3 py-2.5 shadow-lg flex items-start gap-2.5 max-w-[320px] animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-600 text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-red-800 text-xs leading-relaxed font-medium">
                {streamError?.stationName}
              </p>
              <p className="text-red-600 text-[11px] mt-0.5 leading-relaxed">
                {streamError?.message}
              </p>
            </div>
            <button
              onClick={dismissStreamError}
              className="w-5 h-5 rounded-full flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Player Bar — horizontal cuando cabina cerrada */}
      {!isCabinaOpen && (
        <div className={`hidden md:flex fixed z-[60] bottom-4 left-4 right-4 h-[80px] bg-foreground-950/95 backdrop-blur-xl border border-foreground-800/60 rounded-2xl shadow-2xl items-center px-4 lg:px-5 transition-all duration-300`}>
          {/* Fallback Banner — absolute above player */}
          {showFallbackBanner && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-amber-50 border border-amber-200/70 rounded-xl px-3 py-2 shadow-lg flex items-start gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-600 text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground-800 text-xs leading-relaxed">
                  <strong>{fallbackInfo?.originalStationName}</strong> no está transmitiendo en este momento.
                </p>
                <p className="text-foreground-600 text-[11px] mt-0.5 leading-relaxed">
                  Ahora estás escuchando <strong>RFW Radio</strong>.
                </p>
              </div>
              <button
                onClick={() => { setFallbackVisible(false); dismissFallback(); }}
                className="w-5 h-5 rounded-full flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-amber-100 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          )}

          {/* ── ZONE 1: Logo ── */}
          <div className="flex items-center shrink-0 mr-4">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-foreground-800 shrink-0 relative ring-2 ring-foreground-800">
              <img src={currentStation.logoUrl} alt={currentStation.name} className="w-full h-full object-cover" />
              {fallbackInfo && (
                <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                  <i className="ri-shuffle-line text-white text-sm drop-shadow-[0_0_3px_rgba(0,0,0,0.5)]"></i>
                </div>
              )}
            </div>
          </div>

          {/* ── ZONE 2: Info ── */}
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5 mr-4">
            {/* Name + badges */}
            <div className="flex items-center gap-2 min-w-0">
              <h4 className="text-sm font-bold text-background-50 truncate" title={currentStation.name}>
                {currentStation.name}
              </h4>
              {currentStation.isPlatformRadio && (
                <span className="px-1.5 py-0.5 rounded bg-amber-500 text-foreground-950 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                  RFW OFICIAL
                </span>
              )}
              {currentStation.isLive && (
                <span className="flex items-center gap-1 text-accent-400 text-[10px] font-bold uppercase whitespace-nowrap">
                  <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
                  En Vivo
                </span>
              )}
              {fallbackInfo && (
                <span className="text-[10px] text-amber-400 font-medium shrink-0 whitespace-nowrap">· Fallback</span>
              )}
            </div>
            {/* Content */}
            {currentStation.currentMetadata ? (
              <p className="text-background-50 font-semibold text-sm truncate" title={currentStation.currentMetadata}>
                {currentStation.currentMetadata}
              </p>
            ) : (
              <p className="text-foreground-500 text-xs truncate" title={currentStation.frequency}>
                {currentStation.isLive ? 'Transmisión en vivo' : currentStation.frequency}
              </p>
            )}
            {/* Secondary */}
            <p className="text-foreground-500 text-xs truncate" title={currentStation.frequency}>
              {currentStation.frequency}
            </p>
          </div>

          {/* ── ZONE 3: Visualizer (decorative) ── */}
          <div className="hidden xl:flex items-end gap-[3px] h-7 mx-3 shrink-0">
            {[12,18,10,22,14,20,8,16,24,10,18,12,20,14,22,10].map((h, i) => (
              <div
                key={i}
                className={`w-[3px] rounded-full bg-primary-500/50 ${isPlaying ? 'animate-pulse' : ''}`}
                style={{ height: isPlaying ? `${h}px` : '4px', animationDelay: `${i * 0.06}s` }}
              ></div>
            ))}
          </div>

          {/* ── ZONE 4: Controls ── */}
          <div className="flex items-center gap-1.5 shrink-0 mr-3">
            <button
              onClick={toggle}
              className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 cursor-pointer ${
                isPlaying ? 'bg-accent-500 hover:bg-accent-600' : 'bg-primary-500 hover:bg-primary-600'
              }`}
              title={isPlaying ? 'Pausar' : 'Reproducir'}
            >
              <i className={`text-background-50 text-lg ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
            </button>
            <div className="relative" ref={volumeRef}>
              <button
                onClick={() => setShowVolume(!showVolume)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                title="Volumen"
              >
                <i className={`${volumeIcon} text-sm`}></i>
              </button>
              {showVolume && (
                <div className="absolute bottom-full right-0 mb-2 bg-foreground-900 border border-foreground-700 rounded-xl p-3 shadow-2xl flex items-center gap-2 min-w-[160px]">
                  <button
                    onClick={() => setVolume(Math.max(0, volume - 10))}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                  >
                    <i className="ri-volume-down-line text-sm"></i>
                  </button>
                  <div className="flex-1 h-1.5 bg-foreground-700 rounded-full relative cursor-pointer">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      onChange={(e) => setVolume(Number(e.target.value))}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    <div
                      className="absolute inset-y-0 left-0 bg-primary-500 rounded-full transition-all"
                      style={{ width: `${volume}%` }}
                    ></div>
                  </div>
                  <button
                    onClick={() => setVolume(Math.min(100, volume + 10))}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                  >
                    <i className="ri-volume-up-line text-sm"></i>
                  </button>
                  <span className="text-background-50 text-xs font-semibold w-8 text-right">{volume}%</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setExpanded(true)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
              title="Expandir reproductor"
            >
              <i className="ri-fullscreen-line text-sm"></i>
            </button>
          </div>

          {/* ── ZONE 5: Actions ── */}
          <div className="flex items-center gap-1.5 shrink-0 border-l border-foreground-800 pl-3">
            <button
              onClick={stop}
              className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-500 hover:text-accent-500 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
              title="Detener"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Vertical Player — incrustado al lado de la cabina cuando está abierta */}
      {isCabinaOpen && (
        <div className="hidden md:flex fixed z-[55] right-[460px] top-1/2 -translate-y-1/2 w-[72px] bg-foreground-950/95 backdrop-blur-xl border border-foreground-800/60 rounded-2xl shadow-2xl flex-col items-center py-4 gap-3 transition-all duration-300">
          {/* Logo */}
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-foreground-800 relative ring-2 ring-foreground-800 shrink-0">
            <img src={currentStation.logoUrl} alt={currentStation.name} className="w-full h-full object-cover" />
            {fallbackInfo && (
              <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                <i className="ri-shuffle-line text-white text-xs drop-shadow-[0_0_3px_rgba(0,0,0,0.5)]"></i>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-10 h-px bg-foreground-800"></div>

          {/* Play/Pause */}
          <button
            onClick={toggle}
            className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 cursor-pointer ${
              isPlaying ? 'bg-accent-500 hover:bg-accent-600' : 'bg-primary-500 hover:bg-primary-600'
            }`}
            title={isPlaying ? 'Pausar' : 'Reproducir'}
          >
            <i className={`text-background-50 text-lg ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          </button>

          {/* Volumen toggle simple */}
          <button
            onClick={() => setVolume(volume === 0 ? 80 : 0)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
            title={volume === 0 ? 'Activar sonido' : 'Silenciar'}
          >
            <i className={`text-sm ${volume === 0 ? 'ri-volume-mute-line' : volume < 50 ? 'ri-volume-down-line' : 'ri-volume-up-line'}`}></i>
          </button>

          {/* Visualizer mini */}
          <div className="flex items-end gap-[2px] h-6">
            {[10,16,8,20,12,18,6,14].map((h, i) => (
              <div
                key={i}
                className={`w-[2px] rounded-full bg-primary-500/50 ${isPlaying ? 'animate-pulse' : ''}`}
                style={{ height: isPlaying ? `${h}px` : '3px', animationDelay: `${i * 0.08}s` }}
              ></div>
            ))}
          </div>

          {/* Divider */}
          <div className="w-10 h-px bg-foreground-800"></div>

          {/* Expandir */}
          <button
            onClick={() => setExpanded(true)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
            title="Expandir reproductor"
          >
            <i className="ri-fullscreen-line text-sm"></i>
          </button>

          {/* Detener */}
          <button
            onClick={stop}
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-500 hover:text-accent-500 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
            title="Detener"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Stream Error Banner — Mobile */}
      {showStreamErrorBanner && (
        <div className="md:hidden fixed z-[60] bottom-20 right-3">
          <div className="bg-red-50 border border-red-200/70 rounded-xl px-3 py-2 shadow-lg flex items-start gap-2 max-w-[260px] animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-error-warning-line text-red-600 text-xs"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-red-800 text-[11px] leading-relaxed font-medium">
                {streamError?.stationName}
              </p>
              <p className="text-red-600 text-[10px] mt-0.5 leading-relaxed">
                {streamError?.message}
              </p>
            </div>
            <button
              onClick={dismissStreamError}
              className="w-4 h-4 rounded-full flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-[10px]"></i>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Player — oculto cuando la cabina está abierta */}
      {!isCabinaOpen && (
        <div className={`md:hidden fixed z-[60] transition-all duration-300 bottom-20 left-3 right-3`}>
          {/* Fallback Banner Mobile */}
          {showFallbackBanner && (
            <div className="mb-2 bg-amber-50 border border-amber-200/70 rounded-xl px-3 py-2 shadow-lg flex items-start gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-600 text-xs"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground-800 text-[11px] leading-relaxed">
                  <strong>{fallbackInfo?.originalStationName}</strong> no está transmitiendo.
                </p>
                <p className="text-foreground-600 text-[10px] mt-0.5 leading-relaxed">
                  Escuchando <strong>RFW Radio</strong>.
                </p>
              </div>
              <button
                onClick={() => { setFallbackVisible(false); dismissFallback(); }}
                className="w-4 h-4 rounded-full flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-amber-100 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
          )}

          <div className="bg-foreground-950/95 backdrop-blur-xl border border-foreground-800/60 rounded-2xl p-3 shadow-2xl">
            {/* ── ROW 1: Logo + Name + Play + Close ── */}
            <div className="flex items-center gap-3">
              {/* Logo */}
              <div className="w-11 h-11 rounded-xl overflow-hidden bg-foreground-800 shrink-0 relative">
                <img
                  src={currentStation.logoUrl}
                  alt={currentStation.name}
                  className="w-full h-full object-cover"
                />
                {fallbackInfo && (
                  <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                    <i className="ri-shuffle-line text-white text-[10px] drop-shadow-[0_0_3px_rgba(0,0,0,0.5)]"></i>
                  </div>
                )}
              </div>

              {/* Name + Badges */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h4
                    className="text-sm font-bold text-background-50 truncate leading-tight"
                    title={currentStation.name}
                  >
                    {currentStation.name}
                  </h4>
                  {currentStation.isLive && (
                    <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse shrink-0"></span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {currentStation.isPlatformRadio && (
                    <span className="px-1 py-0.5 rounded bg-amber-500 text-foreground-950 text-[9px] font-bold uppercase whitespace-nowrap">
                      RFW OFICIAL
                    </span>
                  )}
                  <p
                    className="text-foreground-400 text-[11px] truncate"
                    title={currentStation.frequency}
                  >
                    {currentStation.frequency}
                  </p>
                </div>
              </div>

              {/* Play/Pause */}
              <button
                onClick={toggle}
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 cursor-pointer ${
                  isPlaying
                    ? 'bg-accent-500 hover:bg-accent-600'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
                title={isPlaying ? 'Pausar' : 'Reproducir'}
              >
                <i
                  className={`text-background-50 text-base ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}
                ></i>
              </button>

              {/* Cerrar */}
              <button
                onClick={stop}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:text-accent-500 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                title="Detener"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>

            {/* ── ROW 2: Content + Artist ── */}
            {currentStation.currentMetadata && (
              <div className="mt-2 flex items-center gap-2 pl-14">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-music-line text-foreground-500 text-xs"></i>
                </span>
                <p
                  className="text-xs text-background-50 truncate flex-1 min-w-0"
                  title={currentStation.currentMetadata}
                >
                  {currentStation.currentMetadata}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Mobile Full Player (bottom sheet) ── */}
      <MobileFullPlayer isOpen={mobileExpanded} onClose={() => setMobileExpanded(false)} />

      {/* ── Modal Expandido (Desktop) ── */}
      {expanded && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

          {/* Modal */}
          <div
            ref={modalRef}
            className="relative bg-foreground-950 border border-foreground-800 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button top-right */}
            <button
              onClick={() => setExpanded(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-foreground-800 hover:bg-foreground-700 text-foreground-400 hover:text-background-50 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            {/* ── Fallback Banner expanded ── */}
            {showFallbackBanner && (
              <div className="mx-5 mt-5 bg-amber-50/90 border border-amber-200/70 rounded-2xl px-4 py-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-amber-600 text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground-800 text-sm leading-relaxed font-medium">
                    {fallbackInfo?.originalStationName} no está transmitiendo en este momento.
                  </p>
                  <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">
                    Ahora estás escuchando <strong>RFW Radio</strong>.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setFallbackVisible(false);
                    dismissFallback();
                  }}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-amber-100 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            )}

            {/* Header visual */}
            <div className="relative h-40 bg-gradient-to-br from-accent-500/20 via-primary-500/10 to-foreground-950 flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-4 left-8 w-32 h-32 rounded-full bg-accent-500/30 blur-3xl"></div>
                <div className="absolute bottom-4 right-8 w-40 h-40 rounded-full bg-primary-500/30 blur-3xl"></div>
              </div>
              <div className="relative">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-foreground-800 shadow-xl border-2 border-foreground-700">
                  <img
                    src={currentStation.logoUrl}
                    alt={currentStation.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {currentStation.isLive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold whitespace-nowrap">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    EN VIVO
                  </span>
                )}
                {fallbackInfo && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold whitespace-nowrap">
                    <i className="ri-shuffle-line text-[10px]"></i>
                    RFW RADIO
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Station Info */}
              <div className="text-center">
                <h2 className="text-xl font-bold text-background-50">
                  {currentStation.name}
                </h2>
                <p className="text-foreground-400 text-sm mt-0.5">
                  {currentStation.frequency}
                  {currentStation.isLive && (
                    <span className="text-accent-400 ml-1.5 text-xs font-bold">
                      · EN VIVO
                    </span>
                  )}
                  {fallbackInfo && (
                    <span className="text-amber-400 ml-1.5 text-xs font-medium">
                      · Fallback automático
                    </span>
                  )}
                </p>
                {currentStation.listeners > 0 && (
                  <p className="text-foreground-500 text-xs mt-1">
                    <i className="ri-headphone-line mr-1"></i>
                    {currentStation.listeners.toLocaleString()} oyentes
                  </p>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                {/* Prev station */}
                <button
                  onClick={() => {
                    const idx = stations.findIndex((s) => s.id === currentStation.id);
                    const prev = idx > 0 ? stations[idx - 1] : stations[stations.length - 1];
                    changeStation(prev);
                  }}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-skip-back-fill text-xl"></i>
                </button>

                {/* Play/Pause grande */}
                <button
                  onClick={toggle}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer shrink-0 ${
                    isPlaying
                      ? 'bg-accent-500 hover:bg-accent-600 shadow-lg shadow-accent-500/30'
                      : 'bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-500/30'
                  }`}
                >
                  <i
                    className={`text-background-50 text-2xl ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}
                  ></i>
                </button>

                {/* Next station */}
                <button
                  onClick={() => {
                    const idx = stations.findIndex((s) => s.id === currentStation.id);
                    const next =
                      idx < stations.length - 1 ? stations[idx + 1] : stations[0];
                    changeStation(next);
                  }}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-skip-forward-fill text-xl"></i>
                </button>
              </div>

              {/* Volume Slider */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setVolume(Math.max(0, volume - 10))}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-volume-down-line text-base"></i>
                </button>
                <div className="flex-1 h-2 bg-foreground-700 rounded-full relative cursor-pointer">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <div
                    className="absolute inset-y-0 left-0 bg-primary-500 rounded-full transition-all"
                    style={{ width: `${volume}%` }}
                  ></div>
                </div>
                <button
                  onClick={() => setVolume(Math.min(100, volume + 10))}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer shrink-0"
                >
                  <i className="ri-volume-up-line text-base"></i>
                </button>
                <span className="text-background-50 text-xs font-semibold w-9 text-right">
                  {volume}%
                </span>
              </div>

              {/* Station Switcher */}
              <div className="relative">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
                  <input
                    type="text"
                    placeholder="Buscar estación..."
                    value={stationSearch}
                    onChange={(e) => setStationSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-foreground-800 border border-foreground-700 rounded-xl text-background-50 text-sm placeholder-foreground-500 outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                {stationSearch && (
                  <div className="mt-2 max-h-48 overflow-y-auto bg-foreground-900 border border-foreground-700 rounded-xl">
                    {filteredStations.length === 0 ? (
                      <p className="text-foreground-500 text-xs text-center py-4">
                        Sin resultados
                      </p>
                    ) : (
                      filteredStations.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => {
                            changeStation(s);
                            setStationSearch('');
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-foreground-800 transition-colors cursor-pointer text-left ${
                            currentStation.id === s.id
                              ? 'bg-primary-500/10 border-l-2 border-primary-500'
                              : 'border-l-2 border-transparent'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden bg-foreground-800 shrink-0">
                            <img
                              src={s.logoUrl}
                              alt={s.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              className={`text-sm font-medium truncate ${
                                currentStation.id === s.id
                                  ? 'text-primary-400'
                                  : 'text-background-50'
                              }`}
                            >
                              {s.name}
                            </p>
                            <p className="text-[11px] text-foreground-500">
                              {s.frequency} · {s.category}
                            </p>
                          </div>
                          {currentStation.id === s.id && (
                            <i className="ri-check-line text-primary-400 text-sm shrink-0"></i>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Link
                  to={`/radio/${currentStation.slug || currentStation.frequency.replace('.', '-')}`}
                  onClick={() => setExpanded(false)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-foreground-800 hover:bg-foreground-700 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  Ir a la Radio
                </Link>
                <button
                  onClick={() => {
                    stop();
                    setExpanded(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-foreground-800 hover:bg-accent-500/20 text-accent-400 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-stop-circle-line"></i>
                  Detener
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}